import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal.jsx';
import { Icon } from './Icon.jsx';
import { PdfPreview } from './PdfPreview.jsx';
import { useLetters } from '../store/lettersStore.js';
import { useT } from '../i18n/index.js';
import { generateLetterPdf } from '../utils/pdfOps.js';

// Generated letter PDFs are cached so re-selecting an entry is instant.
const previewCache = new Map();

// Dedicated modal listing saved letters (the letter-template history),
// with a PDF preview of the selected entry. Load / re-generate / rename
// / delete each entry; arrow keys move the selection.
export function LetterHistoryModal({ open, onClose, onRegenerate, onRename, busy }) {
  const t = useT();
  const templates = useLetters((s) => s.templates);
  const editingId = useLetters((s) => s.editingId);
  const loadTemplate = useLetters((s) => s.loadTemplate);
  const deleteTemplate = useLetters((s) => s.deleteTemplate);

  const [selectedId, setSelectedId] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const rowRefs = useRef({});
  const reqRef = useRef(0);

  // Keep a valid selection — default to the first (newest) entry.
  useEffect(() => {
    if (!open) return;
    if (!templates.some((x) => x.id === selectedId)) {
      setSelectedId(templates[0]?.id ?? null);
    }
  }, [open, templates, selectedId]);

  // Up/Down arrows move the selection through the list.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!templates.length) return;
      e.preventDefault();
      const i = templates.findIndex((x) => x.id === selectedId);
      const ni =
        e.key === 'ArrowDown'
          ? Math.min(templates.length - 1, (i < 0 ? -1 : i) + 1)
          : Math.max(0, (i < 0 ? 0 : i) - 1);
      setSelectedId(templates[ni].id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, templates, selectedId]);

  // Keep the selected row visible.
  useEffect(() => {
    rowRefs.current[selectedId]?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  // Fall back to the first entry so the panel is never blank on the
  // first render (before the sync effect runs).
  const resolvedId = templates.some((x) => x.id === selectedId)
    ? selectedId
    : templates[0]?.id ?? null;
  const selected = templates.find((x) => x.id === resolvedId) || null;

  // Generate (and cache) the PDF preview of the selected letter.
  useEffect(() => {
    const req = ++reqRef.current;
    if (!open || !selected) {
      setPreviewBlob(null);
      setPreviewing(false);
      return;
    }
    const key = `${selected.id}:${selected.date || ''}`;
    const cached = previewCache.get(key);
    if (cached) {
      setPreviewBlob(cached);
      setPreviewing(false);
      return;
    }
    setPreviewing(true);
    setPreviewBlob(null);
    let alive = true;
    (async () => {
      try {
        const blob = await generateLetterPdf({
          ...selected,
          signature: selected.signature || null,
        });
        if (!alive || req !== reqRef.current) return;
        previewCache.set(key, blob);
        setPreviewBlob(blob);
      } catch {
        /* preview is best-effort */
      } finally {
        if (alive && req === reqRef.current) setPreviewing(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, selected]);

  // Load the selected letter into the form, then close.
  const load = (id) => {
    loadTemplate(id);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('letters.history')}
      size="lg"
      footer={
        <button className="btn primary sm" onClick={onClose}>{t('modal.close')}</button>
      }
    >
      {templates.length === 0 ? (
        <div className="empty">{t('letters.no_templates')}</div>
      ) : (
        <div className="history-layout">
          <div className="history-rows">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                ref={(el) => { rowRefs.current[tpl.id] = el; }}
                className={`hist-row ${tpl.id === resolvedId ? 'selected' : ''} ${
                  editingId === tpl.id ? 'is-editing' : ''
                }`}
                onClick={() => setSelectedId(tpl.id)}
              >
                <Icon name="doc" size={15} />
                <span className="hist-row-name" title={tpl.name}>{tpl.name}</span>
              </button>
            ))}
          </div>

          <div className="history-detail">
            {selected ? (
              <>
                <PdfPreview
                  blob={previewBlob}
                  loading={previewing}
                  loadingLabel={t('history.preview_loading')}
                  emptyLabel={t('history.expired')}
                />
                <div className="history-detail-meta">
                  <div className="hist-detail-name" title={selected.name}>{selected.name}</div>
                  {selected.subject && (
                    <div className="history-meta"><span>{selected.subject}</span></div>
                  )}
                </div>
                <div className="history-detail-actions">
                  <button className="btn sm primary" onClick={() => load(selected.id)}>
                    <Icon name="text" size={14} /> {t('history.prefill')}
                  </button>
                  <button
                    className="btn sm"
                    onClick={() => onRegenerate(selected)}
                    disabled={busy}
                  >
                    <Icon name="download" size={14} /> {t('letters.regenerate')}
                  </button>
                  <button className="btn sm" onClick={() => onRename(selected)}>
                    <Icon name="text" size={14} /> {t('letters.rename')}
                  </button>
                  <button
                    className="btn sm danger"
                    onClick={() => deleteTemplate(selected.id)}
                  >
                    <Icon name="trash" size={14} /> {t('letters.delete_template')}
                  </button>
                </div>
                <p className="tiny muted">{t('letters.history_hint')}</p>
              </>
            ) : (
              <div className="empty">{t('history.select_hint')}</div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
